import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { adminApi } from "../lib/api";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ICON_OPTIONS = [
  "Sparkles", "ShieldCheck", "ShieldAlert", "UserCheck", "HeartHandshake",
  "Users", "Briefcase", "Building2", "GraduationCap", "Rocket", "Wrench",
  "Coffee", "Truck", "Wallet", "TrendingUp", "Heart", "Globe2", "Cpu",
];

export const ProductCreateModal = ({ open, onOpenChange, onCreated }) => {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    category: "",
    short_description: "",
    price_usd: "35",
    original_price_usd: "",
    highlights: "",
    icon: "Sparkles",
  });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.short_description) {
      toast.error("Name, category and description are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        tagline: form.tagline.trim() || undefined,
        category: form.category.trim(),
        short_description: form.short_description.trim(),
        price_usd: parseFloat(form.price_usd) || 0,
        original_price_usd: form.original_price_usd
          ? parseFloat(form.original_price_usd)
          : undefined,
        highlights: form.highlights
          .split("\n")
          .map((h) => h.trim())
          .filter(Boolean),
        icon: form.icon,
      };
      const { data } = await adminApi.post("/admin/products", payload);
      toast.success(`Created "${data.product.name}".`);
      setForm({
        name: "",
        tagline: "",
        category: "",
        short_description: "",
        price_usd: "35",
        original_price_usd: "",
        highlights: "",
        icon: "Sparkles",
      });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create product.";
      toast.error(typeof msg === "string" ? msg : "Validation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-create-modal"
        className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A192F] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A059]" />
            New Product Category
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Add a new business plan to your storefront. It will appear on the landing page
            immediately and you can upload files to it from the Products tab.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Product Name" required>
            <input
              data-testid="new-product-name"
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="e.g., Food Truck Business"
              className="aigl-input"
              required
            />
          </Field>

          <Field label="Tagline" hint="Short positioning line shown under the title">
            <input
              data-testid="new-product-tagline"
              type="text"
              value={form.tagline}
              onChange={update("tagline")}
              placeholder="e.g., Roll out a high-margin street food business"
              className="aigl-input"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category" required>
              <input
                data-testid="new-product-category"
                type="text"
                value={form.category}
                onChange={update("category")}
                placeholder="e.g., Food & Beverage"
                className="aigl-input"
                required
              />
            </Field>
            <Field label="Icon">
              <select
                data-testid="new-product-icon"
                value={form.icon}
                onChange={update("icon")}
                className="aigl-input bg-white"
              >
                {ICON_OPTIONS.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Short Description" required>
            <textarea
              data-testid="new-product-description"
              value={form.short_description}
              onChange={update("short_description")}
              placeholder="A concise 1-2 sentence pitch that appears on the product card."
              className="aigl-input min-h-[72px]"
              required
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Price (USD)" required>
              <input
                data-testid="new-product-price"
                type="number"
                step="0.01"
                min="0.01"
                value={form.price_usd}
                onChange={update("price_usd")}
                className="aigl-input"
                required
              />
            </Field>
            <Field
              label="Original Price (USD)"
              hint="Optional — shows as strikethrough. Auto-set to ~40% more if blank."
            >
              <input
                data-testid="new-product-original-price"
                type="number"
                step="0.01"
                min="0.01"
                value={form.original_price_usd}
                onChange={update("original_price_usd")}
                className="aigl-input"
              />
            </Field>
          </div>

          <Field
            label="Highlights"
            hint="One per line. Shown as bullet checks on the product card."
          >
            <textarea
              data-testid="new-product-highlights"
              value={form.highlights}
              onChange={update("highlights")}
              placeholder={"Full business plan\n3-Year financial forecast\nMarketing playbook"}
              className="aigl-input min-h-[100px]"
            />
          </Field>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-[#0A192F]"
              data-testid="new-product-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="new-product-submit"
              className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-50 text-[#0A192F] font-bold px-5 py-2.5 text-xs font-accent transition"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              CREATE PRODUCT
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <div className="font-accent text-[10px] text-slate-500 mb-1.5">
      {label.toUpperCase()}
      {required ? <span className="text-rose-600 ml-1">*</span> : null}
    </div>
    {children}
    {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
  </label>
);
